import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { getMenuAuth } from "src/apis/authApi";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import { deleteSYS4100_R01, disableSYS4100_R01, downloadExcel_SYS4100_R01, getSYS4100_R01AllCount, getSYS4100_R01List, patchSYS4100_R01, postSYS4100_R01 } from "./Api_SYS4100_R01";
import { defaultSYS4100_R01Filter, SYS4100_R01Filter, useSYS4100_R01Store } from "./Store_SYS4100_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { defaultSYS4100_R01Dto, SYS4100_R01Dto } from "./Dto_SYS4100_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateString } from "src/utils/string/stringUtil";
import { refetchUpdateList_Excelgrid } from "src/utils/axios/reFetch";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, fetchFilterConfig_refetch, InsertRowHandler, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import { validateEmployeeCd } from "src/utils/grid/GridValidateContent";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';


interface UserContentProps {
	gridRef: React.RefObject<AgGridReact<SYS4100_R01Dto> | null>;
	setMessage: (message: string) => void;  
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const SYS4100_R01Content = (props: UserContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		SYS4100_R01Filter, setSYS4100_R01Filter,
		SYS4100_R01PastFilter, setSYS4100_R01PastFilter,
		SYS4100_R01List, setSYS4100_R01List, insertSYS4100_R01List, removeSYS4100_R01List,
		deletedSYS4100_R01List, addDeletedSYS4100_R01List, removeDeletedSYS4100_R01List,
		selectedSYS4100_R01List, setSelectedSYS4100_R01List, removeSelectedSYS4100_R01List,
		originalSYS4100_R01List, setOriginalSYS4100_R01List,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = useSYS4100_R01Store();

	const { fetchOrgCdCode, employeeCdList, orgCdList } = useCommonCodeStore();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();

	

	const setAuth = useActionbarAuthStore((state) => state.setAuth);

	// 권한 정보 로드
	useFetchActionbarAuth("SYS4100_R01");

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});

	const [clickable, setClickable] = useState(true);

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedSYS4100_R01List.length === 0 && selectedSYS4100_R01List.length === 0,
			save: SYS4100_R01List.length === 0,
			print: SYS4100_R01List.length === 0,
			excel: SYS4100_R01List.length === 0
		}));
	}, [selectedSYS4100_R01List, deletedSYS4100_R01List, SYS4100_R01List]);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const { isDefaultFilter, isDefaultPastFilter, listLength } = fetchFilterConfig(
			SYS4100_R01Filter,
			SYS4100_R01PastFilter,
			defaultSYS4100_R01Filter,
			SYS4100_R01List.length
		);

		const response = await downloadExcel_SYS4100_R01(
			isDefaultFilter? '' : 'search',
			getParams(SYS4100_R01Filter),
			listLength
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '부서 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [SYS4100_R01Filter, SYS4100_R01List]);

	/**
	 * 사용자명 입력 변경 핸들러
	 * 검색어 입력 시 호출
	 */
	const handleorgNmChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSYS4100_R01Filter({ useYn: e.target.value });
	}

	const getParams = (filter: SYS4100_R01Filter) => {
		const params: Partial<SYS4100_R01Filter> = {
			orgNm: filter.orgNm || undefined,
			useYn: filter.useYn || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getSYS4100_R01AllCount(getParams(SYS4100_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SYS4100_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (fetchType: 'search' | 'refetch' , options?: FetchOptions) => {
        const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		if (fetchType === 'search') {
			setScrollTop(null);
		}
	
		let initialListLength = SYS4100_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setSYS4100_R01List([]);
		}

		const responseData = await getSYS4100_R01List(
			'',
			getParams(SYS4100_R01Filter),
			initialListLength
		);
		let newList = [...responseData.data.data ]
		setSYS4100_R01List(newList);

		const processAllCount = await renewalAllCount();
		if(newList.length > 0) {
			setOriginalSYS4100_R01List(newList);
			setSelectedSYS4100_R01List([]);
			removeDeletedSYS4100_R01List(deletedSYS4100_R01List);
			gridRef.current?.api.ensureIndexVisible(0, 'top');
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			}
		}else{
			// insertSYS4100_R01List([{ ...defaultSYS4100_R01Row }], 0);
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		}
	
		setSYS4100_R01PastFilter(SYS4100_R01Filter);
		setLoadingSpinner(false);
	}, [SYS4100_R01Filter, SYS4100_R01PastFilter, SYS4100_R01List, deletedSYS4100_R01List, gridRef, setMessage, setLoadingSpinner, setOriginalSYS4100_R01List, setSelectedSYS4100_R01List, insertSYS4100_R01List, setScrollTop]);
	
	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		executeDataFetch('search');
	}, [executeDataFetch]);

	const defaultSYS4100_R01Row = useCallback(() => {
		return {
			...defaultSYS4100_R01Dto,
			inserted: true,
		};
	}, [defaultSYS4100_R01Dto, orgCdList, employeeCdList, validateCommonCodeLists]);

	const initContainerData = useCallback(async () => {
		initData();
		setMessage("");
	}, [initData, setMessage]);

	const handleInsertClick = useCallback((e: React.MouseEvent, rowIndex?: number) => {

		const isGridInsert = rowIndex !== undefined;

		InsertRowHandler({
			event: e,
			dataList: SYS4100_R01List,
			rowData: isGridInsert ? SYS4100_R01List[rowIndex] : { ...defaultSYS4100_R01Row(), valueIndex: -1 },
			insertRow: insertSYS4100_R01List,
			defaultRowData: defaultSYS4100_R01Row(),
			...(isGridInsert && { setClickable }),
		});
	}, [SYS4100_R01List, insertSYS4100_R01List, defaultSYS4100_R01Row, setClickable]);

	const validateInsertData = (data: SYS4100_R01Dto, currentRowIndex: number) => {
		if(!validateString(data.orgCd)) {
			showWarning('조직번호를 입력해주세요.'); return false;
		}
		if(!validateString(data.orgNm)) { 
			showWarning('부서명을 입력해주세요.\n조직번호 : ' + data.orgCd); return false;
		}
		if(!validateString(data.orgAdmin)) { 
			showWarning('부서장을 선택해주세요.\n부서명 : ' + data.orgNm); return false;
		} 
		if (!validateEmployeeCd(data.orgAdmin, employeeCdList)) {
			showWarning('부서장이 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		}
		if(data.topOrganiza !== 'Y' && (!validateString(data.upOrgCd))) {
			showWarning('최상위조직이 아니라면 상위조직을 선택해주세요.\n부서명 : ' + data.orgNm); return false;
		}

		//왜인지 모르겠지만 이곳 상위부서의 상위부서코드는 사업장코드랑 연결되어 있다.
		const vaildTopOrganiza = data.upOrgCd !== undefined && data.upOrgCd !== '' ? true : false;
		if(data.topOrganiza === 'Y' && vaildTopOrganiza) {
			showWarning('최상위조직일시, 상위조직은 선택할수 없습니다.\n부서명 : ' + data	.orgNm); 
			return false;
		}

		if(!validateString(data.createDt!.toString())) {
			showWarning('등록일을 선택해주세요.\n부서명 : ' + data.orgNm); return false;
		}
		if(validateString(data.dueDt) && (!validateString(data.mergeOrgCd))) {
			showWarning('통합일을 설정시, 통합팀을 선택해주세요.\n부서명 : ' + data.orgNm); return false;
		}

		// 1. 삭제하기로 표시된 사용자들을 먼저 제외
		const activeUsers = SYS4100_R01List.filter(user => !deletedSYS4100_R01List.includes(user.orgCd));
		// 현재 검증 중인 행의 인덱스를 제외한 나머지 행들
		const otherUsers = activeUsers.filter((_, index) => index !== currentRowIndex);
		
		if (otherUsers.some(user => user.orgCd === data.orgCd)) {
			showWarning("중복된 조직코드가 있습니다.\n조직코드: " + data.orgCd);
			return false;
		}

		return true;
	}
	
	
	const handleSaveClick = useCallback(async () => {

		// 신규 사용자 처리
		const insertedList: SYS4100_R01Dto[] = SYS4100_R01List.filter(row => 
			!deletedSYS4100_R01List.includes(row.orgCd) && 
			row.inserted === true
		);

		// 수정된 사용자만 필터링
		const modifiedList = SYS4100_R01List.filter(row => 
			!deletedSYS4100_R01List.includes(row.orgCd) && 
			row.isModified && !row.inserted
		);

		
        // 수정 사용자 유효성 검증
		let seenMenuSet = new Set<string>();
		let valid = true;
		modifiedList.forEach((value) => {
			let index = SYS4100_R01List.findIndex(user => user.orgCd === value.orgCd);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.orgCd);
		});
		if(!valid) { return; }
        // 신규 사용자 유효성 검증
		seenMenuSet = new Set<string>();
		valid = true;
		insertedList.forEach((value) => {
			let index = SYS4100_R01List.findIndex(user => user.orgCd === value.orgCd);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.orgCd);
		});
		if(!valid) { return; }

		const deleteStringList = deletedSYS4100_R01List.map(item => item.toString());
		const deleteTargetList = SYS4100_R01List.filter(item => deleteStringList.includes(item.valueIndex.toString()));

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			insertedList.length > 0 ? postSYS4100_R01(insertedList) : Promise.resolve(),
			modifiedList.length > 0 ? patchSYS4100_R01(modifiedList) : Promise.resolve(),
			deletedSYS4100_R01List.length > 0 ? Promise.all(deleteTargetList.map(value => deleteSYS4100_R01(value.orgCd))) : Promise.resolve()
		]);

		if(insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('부서정보 저장이 완료되었습니다.');
			setMessage('부서정보 저장이 완료되었습니다.');
		} else if(deleteResponse) {
			showSuccess('부서정보 삭제가 완료되었습니다.');
			setMessage('부서정보 삭제가 완료되었습니다.');
		}


		setTimeout(() => { setMessage(''); }, 5000);
		fetchOrgCdCode();
		await executeDataFetch('refetch', { messageMode: 'none' });

	}, [SYS4100_R01List, deletedSYS4100_R01List, originalSYS4100_R01List, executeDataFetch]);
	
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedSYS4100_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedSYS4100_R01List(deletedSYS4100_R01List);
			removeSelectedSYS4100_R01List();
			setSYS4100_R01List([...SYS4100_R01List]);
		} else if (selectedSYS4100_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedSYS4100_R01List(selectedSYS4100_R01List);
			removeSelectedSYS4100_R01List(selectedSYS4100_R01List);
		}
	}, [selectedSYS4100_R01List, deletedSYS4100_R01List, addDeletedSYS4100_R01List, removeDeletedSYS4100_R01List, removeSelectedSYS4100_R01List, setSYS4100_R01List, SYS4100_R01List]);

	return{
		clickable, setClickable,
		handleExcelClick,
		handleorgNmChange,
		fetchData,
		defaultSYS4100_R01Row,
		initContainerData,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
		buttonDisabled,
	}
}
